import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DjayIdentity, DjayEmotion } from '../../mascot';
import { XPGainAnimation } from '../shared/XPGainAnimation';
import { getProgressToNextLevel, APP_NAME, PORTAL_LABELS } from '../../config/constants';
import { getLearnerNavItems } from './navigationHelpers';
import { dataLayer } from '../../data/dataLayer';
import { useAppStore } from '../../app/hooks/useAppStore';
import { useVariantUnlocks, TOTAL_VARIANTS, useLongPressDiscovered } from '../../mascot';
import { StreakFlame } from '../shared/StreakFlame';
import { StreakMilestoneBurst, getCrossedMilestone } from '../shared/StreakMilestoneBurst';
import { LevelUpShockwave, LEVEL_UP_FILL_MS, LEVEL_UP_BURST_MS } from '../shared/LevelUpShockwave';
import { useStreakInDangerNudge } from '../../app/hooks/useStreakInDangerNudge';
import { soundManager } from '../../utils/soundManager';
import { hapticManager } from '../../utils/hapticManager';
import { ACHIEVEMENTS } from '../progress/AchievementsSection';
import { useAchievementCount } from '../shared/useAchievementCount';
import { getTimeContext } from '../../utils/timeContext';
import { useActiveAssignments } from '../../app/hooks/useActiveAssignments';
interface SidebarProps {
  currentView: string;
  onNavigate: (view: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}
export function Sidebar({
  currentView,
  onNavigate,
  isCollapsed,
  onToggleCollapse
}: SidebarProps) {
  // Pull required state from the global store
  const {
    state,
    currentLearnerDisplayName,
    viewingLearnerId,
    accounts
  } = useAppStore();
  // Derive values from store
  const stats = state.stats;
  const displayName = currentLearnerDisplayName;
  const activeAccountId = accounts?.activeAccountId || '';
  const activeAccount = accounts?.accountsById?.[activeAccountId];
  const activeAccountRole = viewingLearnerId ? 'learner' : activeAccount?.role || 'learner';
  const isLearner = activeAccountRole === 'learner';
  const isTeacherOrParent = activeAccountRole === 'teacher' || activeAccountRole === 'parent';
  // Avatar handling for learners
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (isLearner) {
      const loadAvatar = async () => {
        const prefs = await dataLayer.getLearnerPreferences();
        if (prefs?.avatarUrl) {
          setAvatarUrl(prefs.avatarUrl);
        }
      };
      loadAvatar();
    }
  }, [isLearner, accounts?.activeAccountId]);
  // Long-press discovery now derived from the easter-egg registry
  // (audit S4, ronde 3 2026).
  const hasDiscoveredLongPress = useLongPressDiscovered();
  // SSOT: the sidebar avatar is an "Identity" surface — <DjayIdentity> mirrors
  // the learner's FULL chosen look (variant + accessory + tier) automatically.
  // (Mascot SSOT audit 2026, §5.2 intent A.)
  // Use shared variant unlock hook
  const {
    unlockedCount
  } = useVariantUnlocks(hasDiscoveredLongPress);
  // Use shared achievement count hook (single source of truth)
  const unlockedAchievementsCount = useAchievementCount();
  // Active assignments (audit P1-3) — single source of truth across
  // HomePage, Sidebar and MobileNav. We only need the `hasAny` flag here
  // for the oranje dot on the Toetsen nav-item.
  const {
    hasAny: hasActiveAssignments
  } = useActiveAssignments(isLearner ? activeAccountId : null);
  // Calculate XP progress for the profile section
  const progressToNextLevel = stats?.xp != null ? getProgressToNextLevel(stats.xp).percent : 0;
  // Streak from account state
  const streak = activeAccount?.appState?.streak?.currentTotalStreak || 0;
  // ── Streak milestone burst (Levende Sidebar — App-Feel §3.4) ──
  // When the streak crosses a celebrated threshold (3/7/14/30/60/100),
  // remount StreakMilestoneBurst once via an incrementing key. Detection
  // mirrors the XP-gain pattern — local useRef, no global state. Reloading
  // at-or-above the milestone does NOT refire (initial ref == current).
  const prevStreakRef = useRef<number>(streak);
  const [streakBurst, setStreakBurst] = useState<{
    milestone: number;
    key: number;
  } | null>(null);
  useEffect(() => {
    const prev = prevStreakRef.current;
    const crossed = getCrossedMilestone(prev, streak);
    if (crossed !== null) {
      setStreakBurst(b => ({
        milestone: crossed,
        key: (b?.key ?? 0) + 1
      }));
    }
    prevStreakRef.current = streak;
  }, [streak]);
  // ── Streak-in-danger nudge (App-Feel §16.C) ──
  // Returns true when streak ≥ 3, no activity today, and hour ≥ 17.
  // Cosmetic only — adds a slow pulse-ring on the StreakFlame.
  const {
    inDanger: streakInDanger
  } = useStreakInDangerNudge();
  // ── XP-gain detection (Levende Sidebar — App-Feel §3.4) ──
  // Watch XP changes and trigger a one-shot floating "+X XP" + glow pulse
  // on the sidebar XP bar whenever XP increases. Resets after the animation.
  const currentXp = stats?.xp ?? 0;
  const prevXpRef = useRef<number>(currentXp);
  const [xpGain, setXpGain] = useState<number>(0);
  const [xpPulseKey, setXpPulseKey] = useState<number>(0);
  useEffect(() => {
    const prev = prevXpRef.current;
    if (currentXp > prev) {
      const delta = currentXp - prev;
      setXpGain(delta);
      setXpPulseKey(k => k + 1);
    }
    prevXpRef.current = currentXp;
  }, [currentXp]);
  // ── Level-up shockwave (App-Feel §16.B) ──
  // When the learner crosses a level boundary, choreograph the sidebar
  // XP-bar through three phases:
  //   1. fill  → bar animates to 100% (acknowledges "you completed the prev level")
  //   2. burst → LevelUpShockwave plays on top of the full bar (radial rays + "Level X!")
  //   3. idle  → bar snaps back to the new level's actual progress %
  // We track the level via prevLevelRef and a phased state machine so we can
  // override the bar's width during phases 1+2. After idle returns, the bar's
  // motion.div picks up the real `progressToNextLevel` again.
  const currentLevel = stats?.level ?? 1;
  const prevLevelRef = useRef<number>(currentLevel);
  const [levelUpPhase, setLevelUpPhase] = useState<'idle' | 'filling' | 'bursting'>('idle');
  const [levelUpKey, setLevelUpKey] = useState<number>(0);
  const [levelUpFor, setLevelUpFor] = useState<number>(currentLevel);
  useEffect(() => {
    const prev = prevLevelRef.current;
    if (currentLevel > prev) {
      setLevelUpFor(currentLevel);
      setLevelUpKey(k => k + 1);
      setLevelUpPhase('filling');
      const t1 = setTimeout(() => {
        setLevelUpPhase('bursting');
        // Tactile pulse lands with the visible shockwave (App-Feel §16.B + G9)
        hapticManager.hapticCelebration();
      }, LEVEL_UP_FILL_MS);
      const t2 = setTimeout(() => setLevelUpPhase('idle'), LEVEL_UP_FILL_MS + LEVEL_UP_BURST_MS);
      prevLevelRef.current = currentLevel;
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
    prevLevelRef.current = currentLevel;
  }, [currentLevel]);
  // Effective XP-bar fill: 100% while choreographing the level-up, otherwise
  // the real progress to next level. The bar's motion.div animates the width
  // between values, so the transitions feel intentional rather than jarring.
  const effectiveBarFill = levelUpPhase === 'idle' ? progressToNextLevel : 100;
  // ── View-aware Djay emotion (Levende Sidebar — App-Feel §3.4) ──
  // For a short window after the learner navigates to a meaningful page,
  // Djay reflects that context. After the window expires we fall back to
  // the time/streak baseline so Djay still "lives" between events.
  const [viewEmotionUntil, setViewEmotionUntil] = useState<number>(0);
  const viewEmotionRef = useRef<DjayEmotion | null>(null);
  useEffect(() => {
    if (!isLearner) return;
    const viewToEmotion: Record<string, DjayEmotion> = {
      collection: 'celebrating',
      progress: 'proud',
      subjects: 'thinking',
      assessment: 'encouraging'
    };
    const next = viewToEmotion[currentView];
    if (next) {
      viewEmotionRef.current = next;
      setViewEmotionUntil(Date.now() + 3500);
    }
  }, [currentView, isLearner]);
  // Lightweight tick so the emotion can revert without a global timer
  const [, force] = useState(0);
  useEffect(() => {
    if (viewEmotionUntil === 0) return;
    const t = setTimeout(() => force(n => n + 1), viewEmotionUntil - Date.now());
    return () => clearTimeout(t);
  }, [viewEmotionUntil]);
  // Compose the final emotion: view-specific (transient) → streak/time baseline
  let contextEmotion: DjayEmotion = 'happy';
  if (viewEmotionRef.current && Date.now() < viewEmotionUntil) {
    contextEmotion = viewEmotionRef.current;
  } else {
    // Shared time-of-day window (audit §P2-6) — replaces the inline
    // `hour < 8 || hour > 20` check so Sidebar, MobileNav and FloatingDjay
    // can never drift apart again.
    const {
      djaySleepy
    } = getTimeContext();
    if (djaySleepy) contextEmotion = 'sleeping';else if (streak >= 5) contextEmotion = 'excited';else if (streak === 0) contextEmotion = 'encouraging';
  }
  return <motion.aside className="hidden md:flex flex-col h-full bg-white border-r-2 border-slate-100 sticky top-0 z-30 shadow-[4px_0_24px_rgba(249,115,22,0.03)]" initial={false} animate={{
    width: isCollapsed ? 88 : 280
  }} transition={{
    type: 'spring',
    stiffness: 300,
    damping: 30
  }} data-id="element-1553" data-mp="21znnl9l6c639">
      {/* Logo Section - Playful */}
      <div className="h-24 flex items-center px-7 relative overflow-hidden" data-id="element-1554" data-mp="2knsng5ypn6ws">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-orange-50/50 to-transparent pointer-events-none" data-id="element-1555" data-mp="2pnfaf74cc1is"></div>

        {/* @djay-slot: sidebar-logo */}
        <motion.div className="flex-shrink-0 relative z-10" whileHover={{
        rotate: 10,
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} data-id="element-1556" data-mp="1e8rywlc0lr08">
          <DjayIdentity size="lg" emotion={contextEmotion} data-id="element-1557" data-mp="knqegxkhns0p" />
        </motion.div>

        <AnimatePresence data-id="element-1558" data-mp="6wbt4k33zeqh">
          {!isCollapsed && <motion.span initial={{
          opacity: 0,
          x: -10
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -10
        }} className="font-black text-slate-800 ml-4 text-2xl tracking-tight whitespace-nowrap overflow-hidden relative z-10" data-id="element-1559" data-mp="6vtpv3i14092">
              {APP_NAME}
            </motion.span>}
        </AnimatePresence>
      </div>

      {/* Navigation Items - Chunky & Colorful */}
      <nav className="flex-1 px-4 space-y-2 mt-4" data-id="element-1560" data-mp="1ibs7vwv6pwmf">
        {getLearnerNavItems().map(item => {
        const isActive = currentView === item.id;
        const Icon = item.icon;
        return <motion.button key={item.id} data-tour={`tour-${item.id}`} onClick={() => {
          soundManager.playNavigate();
          onNavigate(item.id);
        }} className={`
                w-full flex items-center gap-3 px-4 py-3 rounded-xl
                transition-colors group relative
                ${isActive ? 'text-orange-700' : 'text-slate-600 hover:bg-slate-100/80'}
              `} whileHover={{
          x: isCollapsed ? 0 : 4
        }} whileTap={{
          scale: 0.98
        }} data-id="element-1561" data-mp="1xeidumfj7o4z">
              {isActive && <motion.div layoutId="activeNavIndicator" className="absolute inset-0 bg-orange-100 rounded-xl border border-orange-200 shadow-sm" initial={false} transition={{
            type: 'spring',
            stiffness: 400,
            damping: 30
          }} data-id="element-1562" data-mp="2l560kdu3wibl" />}
              <div className="relative flex-shrink-0 z-10" data-id="element-1563" data-mp="2uu6miea0iasu">
                <Icon className={`w-6 h-6 transition-transform duration-300 ${isActive ? 'animate-wiggle text-orange-600' : 'group-hover:scale-110'}`} strokeWidth={isActive ? 2.5 : 2} data-id="element-1564" data-mp="2zs86d1f9ie5d" />
                {item.id === 'assessment' && hasActiveAssignments && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-orange-500 border-2 border-white rounded-full" data-id="element-1565" data-mp="3drr5j4q3if5j" />}
              </div>
              <AnimatePresence data-id="element-1566" data-mp="3nic9z8sgadwd">
                {!isCollapsed && <motion.span initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -10
            }} className={`ml-4 whitespace-nowrap overflow-hidden relative z-10 text-base ${isActive ? 'font-black' : 'font-bold'}`} data-id="element-1567" data-mp="3nhkdlvr14c51">
                    {item.label}
                  </motion.span>}
              </AnimatePresence>
            </motion.button>;
      })}
      </nav>

      {/* Mijn Collectie Button */}
      {isLearner && <div className="px-4 mt-2 mb-2" data-id="element-1568" data-mp="1zlm8utky5zkw">
          <motion.button data-tour="tour-collection" onClick={() => {
        soundManager.playNavigate();
        onNavigate('collection');
      }} className={`
              w-full flex items-center gap-3 p-2 rounded-2xl border-2 transition-all group relative overflow-hidden
              ${currentView === 'collection' ? 'border-orange-400 bg-orange-50 ring-2 ring-orange-400 ring-offset-1' : 'border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 hover:border-orange-300 hover:shadow-md'}
            `} whileHover={{
        scale: 1.02
      }} whileTap={{
        scale: 0.98
      }} data-id="element-1569" data-mp="1zmnwryn2agk1">
            <div className="relative flex-shrink-0" data-id="element-1570" data-mp="1y2b7temsvvu9">
              <motion.div animate={{
            y: [0, -3, 0]
          }} transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-1571" data-mp="1y346dsblvf1b">
                <DjayIdentity size="sm" emotion="excited" data-id="element-1572" data-mp="2c2vr0bwmntoa" />
              </motion.div>
            </div>

            <AnimatePresence data-id="element-1573" data-mp="2h0f6tg8z4eic">
              {!isCollapsed && <motion.div initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -10
          }} className="flex-1 text-left overflow-hidden" data-id="element-1574" data-mp="34qrmjdagnfjh">
                  <span className="block font-black text-orange-900 text-sm truncate" data-id="element-1575" data-mp="16trhk42tb65i">
                    Mijn Collectie
                  </span>
                  <span className="block text-[9px] font-bold text-orange-600 truncate" data-id="element-1576" data-mp="12vbww6u7e9v9">
                    {unlockedCount}/{TOTAL_VARIANTS} Varianten •{' '}
                    {unlockedAchievementsCount}/{ACHIEVEMENTS.length}{' '}
                    Achievements
                  </span>
                </motion.div>}
            </AnimatePresence>
          </motion.button>
        </div>}

      {/* Learner Profile Section */}
      {isLearner && !isCollapsed && stats && <div className="mt-auto pt-4 border-t border-gray-200" data-id="element-1577" data-mp="1zlkxdueylpm6">
          <div className="px-4 py-3" data-id="element-1578" data-mp="1ujlsgg3kmkn4">
            {/* Avatar and Name */}
            <div className="flex items-center gap-3 mb-3" data-id="element-1579" data-mp="s7inw4w3m87l">
              {/* Avatar with custom image support */}
              {avatarUrl ? <img src={avatarUrl} alt={displayName} className="w-12 h-12 rounded-full object-cover flex-shrink-0" data-id="element-1580" data-mp="x491mlkfkrpu" /> : <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-blue-500 flex items-center justify-center text-white font-bold text-lg flex-shrink-0" data-id="element-1581" data-mp="164nwocn75l6j">
                  {displayName.charAt(0).toUpperCase()}
                </div>}
              <div className="flex-1 min-w-0" data-id="element-1582" data-mp="ige8j42iglc8">
                <p className="font-semibold text-gray-900 truncate" data-id="element-1583" data-mp="n7wa9uccypy9">
                  {displayName}
                </p>
                <p className="text-xs text-gray-500" data-id="element-1584" data-mp="3lyo9mfoerdzr">Level {stats.level}</p>
              </div>

              {/* Streak Flame + milestone burst overlay + in-danger nudge (§16.C) */}
              <div className="flex-shrink-0 relative" data-id="element-1585" data-mp="3jd82x5hzxr68">
                <StreakFlame streak={streak} size="sm" inDanger={streakInDanger} data-id="element-1586" data-mp="3jcoy8qhcwza5" />
                {streakBurst && <StreakMilestoneBurst key={streakBurst.key} milestone={streakBurst.milestone} onComplete={() => setStreakBurst(null)} data-id="element-1587" data-mp="39dpm2xxdulie" />}
              </div>
            </div>

            {/* XP Progress */}
            <div className="space-y-1 relative" data-id="element-1588" data-mp="2vmb4mvj4isdg">
              <div className="flex items-center justify-between text-xs text-gray-600" data-id="element-1589" data-mp="2qojb5x2tyy75">
                <span data-id="element-1590" data-mp="2lppz8936zf93">XP</span>
                <span data-id="element-1591" data-mp="2lqhjinrc9qk2">
                  {stats.xp} / {getProgressToNextLevel(stats.xp).nextLevelXP}
                </span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden relative" data-id="element-1592" data-mp="2hq7cd7rt8u3m">
                <motion.div className="h-full bg-gradient-to-r from-purple-500 to-blue-500" initial={{
              width: 0
            }} animate={{
              width: `${effectiveBarFill}%`
            }} transition={levelUpPhase === 'filling' ? {
              duration: 0.45,
              ease: 'easeOut'
            } : {
              type: 'spring',
              stiffness: 150,
              damping: 12
            }} data-id="element-1593" data-mp="2cpjkykzo6b8v" />
                {/* Shine pulse — fires once per XP gain */}
                <AnimatePresence data-id="element-1594" data-mp="3cwwvfej38iny">
                  {xpPulseKey > 0 && <motion.div key={xpPulseKey} className="absolute inset-y-0 w-8 pointer-events-none" style={{
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.85), transparent)',
                mixBlendMode: 'screen'
              }} initial={{
                x: '-100%',
                opacity: 0
              }} animate={{
                x: '420%',
                opacity: [0, 1, 0]
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.9,
                ease: 'easeOut'
              }} data-id="element-1595" data-mp="2yvu9d1s3f8d1" />}
                </AnimatePresence>
              </div>
              {/* Floating "+X XP" — fires once per XP gain */}
              {xpGain > 0 && <XPGainAnimation key={xpPulseKey} amount={xpGain} className="left-1/2 -translate-x-1/2 -top-2" onComplete={() => setXpGain(0)} data-id="element-1596" data-mp="3gzxtu8nwb7dp" />}
              {/* Level-up shockwave (§16.B) — fires once per level boundary.
               Sits on a sibling layer so the rays/glow can paint past the
               XP-bar's overflow-hidden clip. Only mounted during the
               fill+burst phases so it does not interfere with idle state. */}
              {levelUpPhase !== 'idle' && <LevelUpShockwave key={levelUpKey} level={levelUpFor} data-id="element-1597" data-mp="35t3a9skofclg" />}
            </div>
          </div>
        </div>}

      {/* Teacher/Parent Profile Section */}
      {isTeacherOrParent && !isCollapsed && <div className="mt-auto pt-4 border-t border-gray-200" data-id="element-1598" data-mp="3fqzl0j9pc5j1">
          <div className="px-4 py-3" data-id="element-1599" data-mp="2s1onrbrk8t9n">
            <div className="flex items-center gap-3 mb-3" data-id="element-1600" data-mp="2s0n2kegtyqe7">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-blue-500 flex items-center justify-center text-white font-bold text-lg flex-shrink-0" data-id="element-1601" data-mp="2n2lhjnzugh03">
                {displayName.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0" data-id="element-1602" data-mp="17c1eyj3vqay9">
                <p className="font-semibold text-gray-900 truncate" data-id="element-1603" data-mp="tjv1j4s42i2i">
                  {displayName}
                </p>
                <p className="text-xs text-gray-500" data-id="element-1604" data-mp="xkybjanh9jvt">
                  {PORTAL_LABELS[activeAccountRole] ?? activeAccountRole}
                </p>
              </div>
            </div>
          </div>
        </div>}

      {/* Collapse Toggle */}
      <button onClick={onToggleCollapse} className="absolute -right-3.5 top-28 w-7 h-7 bg-white border-2 border-slate-200 rounded-full flex items-center justify-center shadow-sm text-slate-400 hover:text-orange-500 hover:border-orange-300 hover:scale-110 transition-all z-40 focus:outline-none" aria-label={isCollapsed ? 'Uitklappen' : 'Inklappen'} data-id="element-1605" data-mp="1q9cb3uu7so9h">
        {isCollapsed ? <ChevronRight className="w-4 h-4 stroke-[3]" data-id="element-1606" data-mp="5s7hn6t9o79q" /> : <ChevronLeft className="w-4 h-4 stroke-[3]" data-id="element-1607" data-mp="27iy51gajcg4o" />}
      </button>
    </motion.aside>;
}